import { useEffect, useRef, useState } from 'react';

type Method = 'typed' | 'drawn' | 'uploaded';

const SCRIPTS = [
  { name: 'Dancing Script', css: "'Dancing Script', 'Snell Roundhand', cursive" },
  { name: 'Homemade Apple', css: "'Homemade Apple', 'Bradley Hand', cursive" },
  { name: 'Sacramento', css: "'Sacramento', 'Apple Chancery', cursive" },
  { name: 'Caveat', css: "'Caveat', 'Marker Felt', cursive" },
];

/**
 * Signature adoption.
 *
 * The adoption confirmation wording is fixed and load-bearing: 15 U.S.C.
 * § 7006(5) turns on the signature being "executed or adopted by a person with
 * the intent to sign". "Adopt", "my signature" and "same legal effect as my
 * handwritten signature" are the phrases doing that work — see
 * docs/legal-templates/signature-adoption-confirmation-v1.0.0.md. Nothing here
 * is pre-adopted and no style is preselected. C-18, C-19, C-21.
 */
export default function SignaturePad({
  defaultName, onAdopt, onCancel, busy,
}: { defaultName: string; onAdopt: (m: Method, png: string) => void; onCancel: () => void; busy: boolean }) {
  const [tab, setTab] = useState<Method>('typed');
  const [name, setName] = useState(defaultName);
  const [style, setStyle] = useState<number | null>(null);   // nothing preselected
  const [drawn, setDrawn] = useState(false);
  const [uploaded, setUploaded] = useState<string | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  useEffect(() => {
    if (tab !== 'drawn') return;
    const c = canvas.current;
    if (!c) return;

    const size = () => {
      const w = c.clientWidth, h = c.clientHeight;
      if (w === 0 || h === 0) return false;      // layout not settled yet
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (c.width === Math.floor(w * dpr) && c.height === Math.floor(h * dpr)) return true;
      c.width = Math.floor(w * dpr);
      c.height = Math.floor(h * dpr);
      const cx = c.getContext('2d');
      if (!cx) return true;
      cx.scale(dpr, dpr);
      cx.lineWidth = 2.8; cx.lineCap = 'round'; cx.lineJoin = 'round'; cx.strokeStyle = '#111418';
      return true;
    };

    if (!size()) requestAnimationFrame(size);
    const ro = new ResizeObserver(() => size());
    ro.observe(c);
    window.addEventListener('orientationchange', size);
    return () => { ro.disconnect(); window.removeEventListener('orientationchange', size); };
  }, [tab]);


  function pos(e: React.PointerEvent) {
    const r = (e.target as HTMLCanvasElement).getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }
  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvas.current?.getContext('2d'); if (!ctx) return;
    const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const ctx = canvas.current?.getContext('2d'); if (!ctx) return;
    const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setDrawn(true);
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvas.current, ctx = c?.getContext('2d');
    if (c && ctx) ctx.clearRect(0, 0, c.width, c.height);
    setDrawn(false);
  };

  /** Renders the typed name to a transparent PNG at print resolution. */
  function typedPng(): string {
    const c = document.createElement('canvas');
    c.width = 900; c.height = 220;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#111418';
    ctx.font = `76px ${SCRIPTS[style ?? 0]!.css}`;
    ctx.textBaseline = 'middle';
    ctx.fillText(name, 20, 120);
    return c.toDataURL('image/png');
  }

  const ready =
    (tab === 'typed' && name.trim().length > 0 && style !== null) ||
    (tab === 'drawn' && drawn) ||
    (tab === 'uploaded' && uploaded !== null);

  function adopt() {
    if (!ready) return;
    if (tab === 'typed') onAdopt('typed', typedPng());
    else if (tab === 'drawn') onAdopt('drawn', canvas.current!.toDataURL('image/png'));
    else onAdopt('uploaded', uploaded!);
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Adopt your signature</h2>

      <div role="tablist" className="row" style={{ gap: '.35rem', justifyContent: 'flex-start' }}>
        {(['typed', 'drawn', 'uploaded'] as Method[]).map((m) => (
          <button key={m} role="tab" aria-selected={tab === m} onClick={() => setTab(m)}
            style={tab === m ? { background: 'var(--accent)', color: 'var(--accent-fg)', borderColor: 'var(--accent)' } : undefined}>
            {m === 'typed' ? 'Type' : m === 'drawn' ? 'Draw' : 'Upload'}
          </button>
        ))}
      </div>

      {tab === 'typed' && (
        <div style={{ marginTop: '1rem' }}>
          <label style={{ display: 'block' }}>
            <span className="muted">Your full name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100}
                   style={{ width: '100%', padding: '.6rem', minHeight: 46, fontSize: '1rem' }} />
          </label>
          <p className="muted" style={{ margin: '.75rem 0 .35rem', fontSize: '.85rem' }}>
            Choose a style:
          </p>
          {SCRIPTS.map((s, i) => (
            <button key={s.name} onClick={() => setStyle(i)} aria-pressed={style === i}
              style={{
                display: 'block', width: '100%', textAlign: 'left', marginBottom: '.4rem',
                padding: '.7rem .9rem', minHeight: 60, fontSize: '1.6rem', fontFamily: s.css,
                borderColor: style === i ? 'var(--accent)' : 'var(--line)',
                borderWidth: style === i ? 2 : 1,
              }}>
              {name || 'Your name'}
            </button>
          ))}
        </div>
      )}

      {tab === 'drawn' && (
        <div style={{ marginTop: '1rem' }}>
          <canvas
            ref={canvas}
            onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
            style={{
              width: '100%', height: 200, border: '1px dashed #9aa4af', borderRadius: 8,
              touchAction: 'none', cursor: 'crosshair',
              // Fixed white, never themed — see the ink comment above.
              background: '#ffffff',
              WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none',
            }}
          />
          <div className="row" style={{ justifyContent: 'space-between', marginTop: '.4rem' }}>
            <span className="muted" style={{ fontSize: '.8rem' }}>Draw with your finger, stylus or mouse.</span>
            <button onClick={clear}>Clear</button>
          </div>
        </div>
      )}

      {tab === 'uploaded' && (
        <div style={{ marginTop: '1rem' }}>
          <input type="file" accept="image/png,image/jpeg" onChange={(e) => {
            const f = e.target.files?.[0]; if (!f) return;
            if (f.size > 2_000_000) { alert('Please choose an image under 2 MB.'); return; }
            const fr = new FileReader();
            fr.onload = () => {
              const img = new Image();
              img.onload = () => {
                const c = document.createElement('canvas');
                const scale = Math.min(900 / img.width, 220 / img.height, 1);
                c.width = img.width * scale; c.height = img.height * scale;
                c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
                setUploaded(c.toDataURL('image/png'));
              };
              img.src = String(fr.result);
            };
            fr.readAsDataURL(f);
          }} />
          {uploaded && <img src={uploaded} alt="Your uploaded signature" style={{ maxWidth: '100%', marginTop: '.75rem', border: '1px solid var(--line)', borderRadius: 6 }} />}
        </div>
      )}

      <p style={{ marginTop: '1.25rem', fontSize: '.95rem', lineHeight: 1.5 }}>
        <strong>
          By selecting “Adopt and Sign”, I agree that the signature shown above will be the
          electronic representation of my signature for all purposes in this transaction — and that
          it has the same legal effect as my handwritten signature on paper.
        </strong>
      </p>

      <div className="row" style={{ gap: '.5rem', justifyContent: 'flex-start', marginTop: '1rem' }}>
        <button className="primary" style={{ width: 'auto' }} disabled={!ready || busy} onClick={adopt}>
          {busy ? 'Adopting…' : 'Adopt and Sign'}
        </button>
        <button onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      {!ready && (
        <p className="muted" style={{ fontSize: '.8rem', marginTop: '.5rem' }}>
          {tab === 'typed' ? 'Enter your name and choose a style.'
            : tab === 'drawn' ? 'Draw your signature above.' : 'Choose an image.'}
        </p>
      )}
    </div>
  );
}
